import { BlockBlobClient } from '@azure/storage-blob';
import { runWithConcurrencyPool } from './concurrency-pool';
import { decodeMd5HashFromBase64, MAX_RETRIES } from './utils';

export const DEFAULT_CHUNKED_UPLOAD_CONCURRENCY = 4;

const CHUNK_UPLOAD_RETRY_DELAYS_MS = [0, 2000, 5000];

export const isInvalidBlobContentError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
        ? error.message
        : '';

  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String(error.code)
      : '';

  return (
    code === 'InvalidBlobOrBlock' ||
    /blob or block content is invalid/i.test(message)
  );
};

export interface ChunkedUploadOptions {
  file: File;
  sasUrl: string;
  blockSize: number;
  /** Base64 MD5 from init session — applied on commit like project uploadData. */
  md5HashBase64?: string;
  concurrency?: number;
  completedBlockIndices?: ReadonlySet<number>;
  onProgress?: (loaded: number, total: number) => void;
  onBlockComplete?: (blockIndex: number) => void;
  abortSignal?: AbortSignal;
}

export interface ChunkedUploadResult {
  blockIds: string[];
}

/** Azure block IDs must be equal-length base64 strings. */
export const encodeBlockId = (index: number, totalBlocks: number): string => {
  const padded = index.toString().padStart(totalBlocks.toString().length, '0');

  return btoa(padded);
};

const uploadBlockWithRetry = async (
  blobClient: BlockBlobClient,
  blockId: string,
  chunk: Blob,
  abortSignal?: AbortSignal
): Promise<void> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0)
      await new Promise(resolve =>
        setTimeout(resolve, CHUNK_UPLOAD_RETRY_DELAYS_MS[attempt] ?? 5000)
      );

    if (abortSignal?.aborted)
      throw new DOMException('Upload aborted', 'AbortError');

    try {
      await blobClient.stageBlock(blockId, chunk, chunk.size, {
        abortSignal,
      });

      return;
    } catch (error) {
      lastError = error;

      if (error instanceof DOMException && error.name === 'AbortError')
        throw error;

      if (isInvalidBlobContentError(error)) throw error;
    }
  }

  throw lastError;
};

export const uploadFileInChunks = async ({
  file,
  sasUrl,
  blockSize,
  md5HashBase64,
  concurrency = DEFAULT_CHUNKED_UPLOAD_CONCURRENCY,
  completedBlockIndices,
  onProgress,
  onBlockComplete,
  abortSignal,
}: ChunkedUploadOptions): Promise<ChunkedUploadResult> => {
  const totalBlocks = Math.ceil(file.size / blockSize);
  const blockIds = Array.from({ length: totalBlocks }, (_, index) =>
    encodeBlockId(index, totalBlocks)
  );
  let loadedBytes = completedBlockIndices
    ? blockIds.reduce((total, _blockId, blockIndex) => {
        if (!completedBlockIndices.has(blockIndex)) return total;

        const start = blockIndex * blockSize;
        const end = Math.min(start + blockSize, file.size);

        return total + (end - start);
      }, 0)
    : 0;

  onProgress?.(loadedBytes, file.size);

  const blobClient = new BlockBlobClient(sasUrl);

  await runWithConcurrencyPool(
    blockIds.map((blockId, blockIndex) => ({ blockId, blockIndex })),
    () => concurrency,
    async ({ blockId, blockIndex }) => {
      const start = blockIndex * blockSize;
      const end = Math.min(start + blockSize, file.size);
      const chunk = file.slice(start, end);

      if (completedBlockIndices?.has(blockIndex)) {
        return;
      }

      await uploadBlockWithRetry(blobClient, blockId, chunk, abortSignal);

      loadedBytes += chunk.size;
      onProgress?.(loadedBytes, file.size);
      onBlockComplete?.(blockIndex);
    }
  );

  const blobHTTPHeaders: {
    blobContentType: string;
    blobContentMD5?: Uint8Array;
  } = {
    blobContentType: file.type || 'application/octet-stream',
  };

  if (md5HashBase64) {
    blobHTTPHeaders.blobContentMD5 = decodeMd5HashFromBase64(md5HashBase64);
  }

  await blobClient.commitBlockList(blockIds, { blobHTTPHeaders });

  return { blockIds };
};
